import { Component, Input } from '@angular/core';
import { Store } from '@ngrx/store';
import { map, Observable } from 'rxjs';

import {
  selectHoodTypeKeys,
  selectProjectEntityNotes,
  selectProjectFeedbackMessages
} from '../../store/project-data/project-data.selectors';
import { selectUIProductSidebarOpened } from '../../store/product-view/product-view.selectors';
import { ProductTreeTraverseService } from 'src/app/core/services/productTreeTraverse.service';
import type { EntityNote } from '../../../core/models/entity-note';
import type { TreeEntity } from '../../../core/models/entity.model';
import { PropertyType } from 'src/app/core/models/property-type.model';
import { selectEntityType, selectPropertyTypes } from 'src/app/core/store/entity-types/entity-types.selectors';
import { TranslateService } from '@ngx-translate/core';
import { EntityType } from 'src/app/core/models/entity-type.model';
import { EntityTypeKey } from 'src/app/shared/constants/entity-type-keys';

/**
 * Property name where the entity error name is found
 */
const errorNamePropertyKey = 'UIErrorName';

@Component({
  selector: 'app-product-messages',
  templateUrl: './product-messages.component.html',
  styleUrls: ['./product-messages.component.scss'],
  standalone: false
})
export class ProductMessagesComponent {
  @Input() messageFilter: (message: TreeEntity) => boolean;

  feedbackMessages$: Observable<TreeEntity[]>;
  notes$: Observable<EntityNote[]>;
  productSidebarOpened$: Observable<boolean>;
  hoodTypeKeys$: Observable<string[]>;
  propertyTypes$: Observable<Record<string, PropertyType>>;
  hoodEntityType$: Observable<EntityType>;

  constructor(private store: Store, private translate: TranslateService) {
    this.feedbackMessages$ = this.store.select(selectProjectFeedbackMessages).pipe(
      map(message => {
        if (this.messageFilter) {
          return message.filter(this.messageFilter);
        }
        return message;
      })
    );

    this.notes$ = this.store.select(selectProjectEntityNotes);
    this.productSidebarOpened$ = this.store.select(selectUIProductSidebarOpened);
    this.hoodTypeKeys$ = this.store.select(selectHoodTypeKeys);
    this.propertyTypes$ = this.store.select(selectPropertyTypes);
    this.hoodEntityType$ = this.store.select(selectEntityType(EntityTypeKey.GastroFlowHood));
  }

  /**
   * Creates a "shortcode" to be displayed with an error message. This allows the target entity to be identified by the user.
   * For example: Hood 1, Module 2, Supply device would result in "H1M2S".
   * @param entity
   * @param hoodTypeKeys
   */
  getMessageTargetCode(entity: TreeEntity, hoodTypeKeys: string[]) {
    // Get the listing of all parents up to the hood
    const parentGraph = ProductTreeTraverseService.findParentGraphUpToHood(entity, hoodTypeKeys);

    const entityTags = parentGraph
      .map(entity => {
        const errorNameProperty = entity.Properties[errorNamePropertyKey];
        if (!errorNameProperty) {
          return null;
        }
        return errorNameProperty.Value;
      })
      .filter(x => !!x);

    // Filter out empty values and nulls, reverse the list (child->parent to parent->child) order, then create a string out of it
    return entityTags.reverse().join('');
  }

  isPriceDialogMessage(message: TreeEntity, hoodEntityType: EntityType) {
    const propertyKey = message.Properties['PropertyKey'].Value as string;

    if (hoodEntityType) {
      const budgetPricesGroup = hoodEntityType.Groups.find(x => x.Key === 'BudgetPrices');

      return budgetPricesGroup.Properties.some(x => x.Key === propertyKey);
    }

    return false;
  }

  getPropertyName(message: TreeEntity, types: Record<string, PropertyType>) {
    const propertyKey = message.Properties['PropertyKey'].Value as string;

    if (propertyKey) {
      const nameId = types[propertyKey]?.NameId ?? '';

      return this.translate.instant(nameId);
    } else {
      return this.translate.instant(`${message.Parent.EntityTypeKey}.${message.Parent.EntityTypeKey}`);
    }
  }
}
